import type { LCIAResult } from "@/types/Views/MaterialDatabase/Material";

// Module index → life-cycle stage, mirroring the EPD source data (see EmissionsPanel.vue).
// 0 is the combined A1–A3 entry; 1/2/3 are the individual A1/A2/A3 modules.
const COMBINED_A1A3_MODULE = 0;
const INDIVIDUAL_A1A3_MODULES = [1, 2, 3];
// B4 (replacement). Its project-level value depends on the material's lifetime and the project's
// consideration period, which an EPD knows nothing about — see sumEpdStagesPerDeclaredUnit.
const REPLACEMENT_MODULE = 9;

// Raw module index → life-cycle stage name. The individual A1/A2/A3 modules collapse into A1A3 for
// display, so their names here are only ever used as a lookup fallback.
const MODULE_STAGE_NAMES: Record<number, string> = {
  0: "A1A3",
  1: "A1",
  2: "A2",
  3: "A3",
  4: "A4",
  5: "A5",
  6: "B1",
  7: "B2",
  8: "B3",
  9: "B4",
  10: "B5",
  11: "B6",
  12: "B7",
  13: "C1",
  14: "C2",
  15: "C3",
  16: "C4",
  17: "D",
};

// The total-GWP indicator, by its EPD identifier. "gwp" is the EN15804+A1 total; "gwp-total" /
// "gwptotal" cover the +A2 spelling. Deliberately excludes the A2 sub-indicators (gwp-fossil,
// gwp-biogenic, gwp-luluc) so we never sum them into the headline figure.
const GWP_TOTAL_IDENTIFIERS = new Set(["gwp", "gwp-total", "gwptotal"]);

const isGwpTotal = (indicator: LCIAResult["indicators"][number]): boolean =>
  indicator.originalIdentifier != null && GWP_TOTAL_IDENTIFIERS.has(indicator.originalIdentifier.toLowerCase());

export interface IndicatorFigure {
  value: number;
  unit?: string;
}

/**
 * Sum the total-GWP indicator's per-declared-unit value across the A1–A3 (cradle-to-gate) modules
 * of an EPD's LCIA results. Mirrors the EPD-level aggregation in EmissionsPanel.vue: prefer the
 * combined A1A3 entry (module 0) when it carries the indicator; otherwise fold the individual
 * A1/A2/A3 modules (1/2/3) so those stages aren't dropped. The GWP indicator is found by its EPD
 * identifier, so the figure is project-independent (works in the global Library too). Returns null
 * when there is no A1–A3 GWP data (callers hide the figure rather than show a misleading zero).
 */
export function sumGwpA1A3(lciaResults: LCIAResult[] | undefined | null): IndicatorFigure | null {
  if (!lciaResults?.length) return null;

  const hasCombined = lciaResults.some(
    (lcia) => lcia.moduleTypeEnum === COMBINED_A1A3_MODULE && lcia.indicators?.some(isGwpTotal)
  );

  let value = 0;
  let unit: string | undefined;
  let found = false;

  for (const lcia of lciaResults) {
    const included = hasCombined
      ? lcia.moduleTypeEnum === COMBINED_A1A3_MODULE
      : INDIVIDUAL_A1A3_MODULES.includes(lcia.moduleTypeEnum);
    if (!included) continue;

    const datapoint = lcia.indicators?.find(isGwpTotal);
    if (!datapoint) continue;

    value += datapoint.value ?? 0;
    unit = unit ?? datapoint.unit ?? undefined;
    found = true;
  }

  return found ? { value, unit } : null;
}

/**
 * Compact an EPD indicator unit for display next to a value: "kg CO₂ eq." → "kg CO₂e".
 */
export function shortenIndicatorUnit(unit: string): string {
  return unit.replace(/\s*eq\.?/i, "e");
}

export interface EpdStageEmissions {
  /** One indicator's value per life-cycle stage, keyed by stage name (A1A3 · A4 · A5 · B1–B7 · C1–C4 · D). */
  stageValues: Record<string, number>;
  /** The indicator's own unit as declared in the EPD, e.g. "kg CO₂ eq.". Undefined when it carries none. */
  unit?: string;
  /** True when module B4 (replacement) contributed a non-zero value — see the note below. */
  hasReplacementStage: boolean;
}

/**
 * Sum one indicator's modules from an EPD's LCIA results into per-stage values expressed **per one
 * declared unit** of that EPD.
 *
 * This exists because the values on `material.mapping.lciaResults` are NOT per declared unit: the
 * API overwrites them with the material's project-scaled contribution (BuildingMaterialService →
 * ProjectMaterialEmissionCalculator.SumEmissionsByLciaResults), which multiplies by the project
 * quantity and then divides by the calculation type's denominator — so for BR18 they arrive as
 * kg CO₂-eq/m²/year whatever the EPD is declared in (RTLCA-5350). Feed this the EPD as fetched
 * straight from the EPD service (`GET api/material/{id}` / `api/construction/{id}`), which has no
 * project, area or consideration period and is therefore unscaled by construction.
 *
 * `declaredValue` is the amount the EPD declares its results for (usually 1, but e.g. 1000 for a
 * per-tonne EPD); dividing by it normalises every module to exactly one declared unit, which is the
 * same first step the API takes in `CalculateScaledEmission`.
 *
 * Values are returned exactly as the EPD declares them otherwise — no quantity, no lifetime and no
 * consideration period. That matters for B4, which the API multiplies by a replacement count derived
 * from the material's lifetime and the project's consideration period; `hasReplacementStage` lets the
 * caller say so rather than implying the figure is a whole-life one.
 */
export function sumEpdStagesPerDeclaredUnit(
  lciaResults: LCIAResult[] | undefined | null,
  options: { indicatorType?: number; includedPhases: number[]; declaredValue?: number | null }
): EpdStageEmissions {
  const { indicatorType, includedPhases, declaredValue } = options;

  if (!lciaResults?.length) return { stageValues: {}, hasReplacementStage: false };

  const divisor = typeof declaredValue === "number" && Number.isFinite(declaredValue) && declaredValue > 0 ? declaredValue : 1;

  const matchesIndicator = (indicator: LCIAResult["indicators"][number]) => indicator.type === indicatorType;

  // Prefer the combined A1A3 (module 0) entry when it exists and is included; only then are the
  // individual A1/A2/A3 modules (1/2/3) skipped to avoid double counting. If no combined entry is
  // present, fold modules 1/2/3 into A1A3 instead of dropping those stages entirely.
  const hasCombinedA1A3 =
    includedPhases.includes(COMBINED_A1A3_MODULE) &&
    lciaResults.some((lcia) => lcia.moduleTypeEnum === COMBINED_A1A3_MODULE && lcia.indicators?.some(matchesIndicator));

  const stageValues: Record<string, number> = {};
  let unit: string | undefined;
  let hasReplacementStage = false;

  for (const lcia of lciaResults) {
    if (hasCombinedA1A3 && INDIVIDUAL_A1A3_MODULES.includes(lcia.moduleTypeEnum)) continue;
    if (!includedPhases.includes(lcia.moduleTypeEnum)) continue;

    const stageName = INDIVIDUAL_A1A3_MODULES.includes(lcia.moduleTypeEnum) ? "A1A3" : MODULE_STAGE_NAMES[lcia.moduleTypeEnum];
    if (!stageName) continue;

    const datapoint = lcia.indicators?.find(matchesIndicator);
    if (!datapoint) continue;

    const value = (datapoint.value ?? 0) / divisor;
    stageValues[stageName] = (stageValues[stageName] ?? 0) + value;
    if (!unit && datapoint.unit) unit = datapoint.unit;
    if (lcia.moduleTypeEnum === REPLACEMENT_MODULE && value !== 0) hasReplacementStage = true;
  }

  return { stageValues, unit, hasReplacementStage };
}
